/**
 * Tiered relevance search — the shared 7-tier ranking core consumed by both
 * the V2 AI tool (`apps/api/src/domains/ai/product/service.ts` via
 * `rankCandidates`) and the frontend product/partner pickers (called as
 * `rankedFuzzyFilter` — see the alias re-export in `./index.ts`).
 *
 * Tier 0: exact-code   — code equality, separator-insensitive
 * Tier 1: exact-field  — any text field equals the query
 * Tier 2: code-prefix  — code starts with query, separator-insensitive
 * Tier 3: word-prefix  — every query word starts a word in target text
 * Tier 4: substring    — every query word is a substring of target text
 * Tier 5: token-match  — partial word-boundary scoring (+2 word-equal, +1 word-prefix)
 * Tier 6: fuzzy        — Fuse.js typo tolerance with per-word averaging for multi-word
 *
 * The 7 tiers fold into the legacy 5-tier API exposed by `rankCandidates`:
 *   exact-code / exact-field → "exact"
 *   code-prefix / word-prefix → "starts-with"
 *   substring  / token-match  → "contains"
 *   fuzzy                     → "fuzzy"
 * The legacy `classifyDeterministic` keeps that mapping for direct callers.
 *
 * The per-query pipeline (variant parsing → deterministic classification →
 * fuzzy scoring → finalize) lives in `./tieredSearchInternals.ts`, shared
 * verbatim with the chunked `createTimeSlicedSearchIndex` in
 * `./timeSlicedSearch.ts` (STK-1399) so both paths rank identically.
 */
import Fuse from "fuse.js"
import {
  buildQueryVariants,
  classifyPreparedItems,
  collectFuseScores,
  createTierBuckets,
  finalizeTieredResults,
  prepareItems,
  type PreparedItem,
  type TieredSearchConfig,
  type TieredSearchResult,
} from "./tieredSearchInternals"

// Public API surface — historically defined in this module; re-exported so
// existing import paths (`./tieredSearch` and the package barrel) are stable.
export {
  prepareItems,
  tokenize,
  tokenMatchScore,
  type PreparedItem,
  type TieredMatch,
  type TieredSearchConfig,
  type TieredSearchResult,
} from "./tieredSearchInternals"

/**
 * Tiered relevance search: filter + rank in a single pass.
 *
 * Within each tier, items are sub-sorted by Fuse.js score (lower = closer),
 * then by the optional `tiebreaker`. Tier 5 sorts by token score first.
 * Pass a `prebuiltFuse` index to avoid rebuilding it across keystrokes, and
 * `prepared` (from {@link prepareItems}) to avoid re-tokenizing the corpus on
 * every call — both should be memoized by the caller on the item-list identity.
 */
export function tieredSearch<T>(
  items: ReadonlyArray<T>,
  query: string,
  config: TieredSearchConfig<T>,
  tiebreaker?: (a: T, b: T) => number,
  prebuiltFuse?: Fuse<T>,
  prepared?: ReadonlyArray<PreparedItem<T>>
): TieredSearchResult<T>[] {
  const q = query.trim()
  if (!q) {
    return items.map((item) => ({
      item,
      tier: "exact-field" as const,
      tierIndex: 1,
    }))
  }

  const variantData = buildQueryVariants(q, config.normalizeNumbers !== false)

  // Per-item derived strings are computed once via `prepareItems` (passed in
  // as `prepared` by callers that search the same corpus repeatedly) instead
  // of being re-tokenized on every call. When absent (e.g. one-off AI-tool
  // ranking), fall back to preparing inline — identical behavior.
  const prep = prepared ?? prepareItems(items, config)

  const buckets = createTierBuckets<T>()
  classifyPreparedItems(prep, variantData, buckets)

  const fuse =
    prebuiltFuse ??
    new Fuse(items, {
      ...config.fuseOptions,
      includeScore: true,
    })
  const fuseScoreMap = new Map<T, number>()
  collectFuseScores(fuse, variantData, fuseScoreMap)

  // When `prebuiltFuse` is stale (caller cached over a larger set), restrict
  // fuzzy-tier membership to the current `items`.
  return finalizeTieredResults(
    buckets,
    fuseScoreMap,
    tiebreaker,
    prebuiltFuse ? new Set(items) : null
  )
}
